import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import MUIDataTable from "mui-datatables";
import CircularProgress from "@mui/material/CircularProgress";
import DatePicker from "react-datepicker";
import axios from "axios";
import moment from "moment/moment";
import toast, { Toaster } from "react-hot-toast";
import { FeedbackDetailsModal } from "../ModalImpl/FeedbackDetailsModal";

const AllFeedbacks = () => {
  const navigate = useNavigate();

  const baseUrl = localStorage.getItem("API_URL");
  const token = localStorage.getItem("user_token");
  const userDetails = JSON.parse(localStorage.getItem("user_details"));

  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());
  const [allBranchesList, setAllBranchesList] = useState([]);
  const [branchDropDValue, setBranchDropDValue] = useState("");
  const [selectedBranchId, setSelectedBranchId] = useState(
    userDetails?.bid || null
  );
  const [showFeedbackDetailsModal, setShowFeedbackDetailsModal] = useState(false);
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [feedbackDetails, setFeedbackDetails] = useState(null);
  const [isDetailsLoading, setIsDetailsLoading] = useState(false);

  const [list, setList] = useState([]);
  const [count, setCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  // Backend calls Sort.by(direction, name) unconditionally, which throws if
  // name is empty - always keep a valid default column here.
  const [sort, setSort] = useState({ name: "givenDate", direction: "DESC" });

  // get all level 4 branches, same service used on the Branch Management page
  useEffect(() => {
    if (userDetails !== null) {
      axios({
        method: "GET",
        url: `${baseUrl}/branch/get-branches/customer/${userDetails.cid}`,
        headers: {
          Authorization: localStorage.getItem("TS_GEN_TOKEN"),
        },
      })
        .then((res) => {
          const data = res.data.data || [];
          setAllBranchesList(data);

          // Default the selector to the first branch in the list.
          if (data.length > 0) {
            setBranchDropDValue("0");
            setSelectedBranchId(data[0].id);
          }
        })
        .catch((error) => {
          console.log(error);

          if (error.response !== undefined) {
            toast.remove();
            toast.error(error.response.data.message, {
              position: "bottom-center",
            });
          } else {
            toast.remove();
            toast.error("Something went wrong", {
              position: "bottom-center",
            });
          }
        });
    }
  }, []);

  useEffect(() => {
    fetchData(page, rowsPerPage);
  }, [page, rowsPerPage, sort, startDate, endDate, selectedBranchId]);

  const fetchData = (page, rowsPerPage) => {
    setIsLoading(true);
    axios({
      method: "POST",
      url: `${baseUrl}/feedback/get-all-feedbacks`,
      headers: {
        Authorization: token,
      },
      data: {
        branchId: selectedBranchId || userDetails?.bid,
        fromDate: moment(startDate).format("YYYY-MM-DD"),
        toDate: moment(endDate).format("YYYY-MM-DD"),
        page: page,
        size: rowsPerPage,
        sort: sort,
      },
    })
      .then((res) => {
        const data = res.data.data;
        setList(data?.content || []);
        setCount(data?.totalElements || 0);
        setSelectedIndex(null);
      })
      .catch((error) => {
        console.log(error);

        if (error.response !== undefined) {
          toast.remove();
          toast.error(error.response.data.message, {
            position: "bottom-center",
          });
        } else {
          toast.remove();
          toast.error("Something went wrong", {
            position: "bottom-center",
          });
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const getAnswerDetails = (answerId) => {
    setIsDetailsLoading(true);
    setFeedbackDetails(null);
    axios({
      method: "GET",
      url: `${baseUrl}/feedback/answer-details/${answerId}`,
      headers: {
        Authorization: token,
      },
    })
      .then((res) => {
        setFeedbackDetails(res.data.data);
      })
      .catch((error) => {
        console.log(error);

        if (error.response !== undefined) {
          toast.remove();
          toast.error(error.response.data.message, {
            position: "bottom-center",
          });
        } else {
          toast.remove();
          toast.error("Something went wrong", {
            position: "bottom-center",
          });
        }
      })
      .finally(() => {
        setIsDetailsLoading(false);
      });
  };

  const handleStartDateChange = (date) => {
    if (endDate && date > endDate) {
      toast.remove();
      toast.error("From date must not be greater than To date", {
        position: "bottom-center",
      });
      return;
    }
    setStartDate(date);
    setPage(0);
  };

  const handleEndDateChange = (date) => {
    if (startDate && date < startDate) {
      toast.remove();
      toast.error("To date must not be less than From date", {
        position: "bottom-center",
      });
      return;
    }
    setEndDate(date);
    setPage(0);
  };

  const columns = [
    { label: "Question", name: "question" },
    { label: "Feedback", name: "name", options: { sort: false } },
    {
      label: "Image",
      name: "image",
      options: {
        sort: false,
        filter: false,
        customBodyRenderLite: (dataIndex) => {
          const item = list[dataIndex];
          return <h5 className="mb-0">{item?.image}</h5>;
        },
      },
    },
    {
      label: "Given Date",
      name: "givenDate",
      options: {
        sort: false,
        filter: false,
        customBodyRenderLite: (dataIndex) => {
          const item = list[dataIndex];
          return (
            <span>
              {item?.givenDate
                ? moment(item.givenDate).format("YYYY-MM-DD")
                : "-"}
            </span>
          );
        },
      },
    },
    {
      label: "Given Time",
      name: "givenTime",
      options: {
        sort: false,
        filter: false,
        customBodyRenderLite: (dataIndex) => {
          const item = list[dataIndex];
          return (
            <span>
              {item?.givenTime ? item.givenTime : "-"}
            </span>
          );
        },
      },
    },
    { label: "Kiosk", name: "kiosk", options: { sort: false } },
    {
      label: "Details",
      name: "details",
      options: {
        sort: false,
        filter: false,
        customBodyRenderLite: (dataIndex) => {
          const item = list[dataIndex];
          const isSelected = selectedIndex === dataIndex;
          return (
            <button
              type="button"
              className={`btn btn-sm ${
                isSelected ? "btn-outline-primary" : "btn-primary"
              }`}
              onClick={() => {
                setSelectedIndex(dataIndex);
                setSelectedFeedback({
                  ...item,
                  givenDate: item?.givenDate
                    ? moment(item.givenDate).format("YYYY-MM-DD")
                    : "",
                  givenTime: item?.givenTime
                    ? moment(item.givenTime).format("hh:mm A")
                    : "",
                });
                setShowFeedbackDetailsModal(true);
                if (item?.id !== undefined && item?.id !== null) {
                  getAnswerDetails(item.id);
                }
              }}
            >
              {isSelected ? "Viewed" : "View Details"}
            </button>
          );
        },
      },
    },
  ];

  const options = {
    filterType: "dropdown",
    filter: false,
    responsive: "standard",
    serverSide: true,
    count: count,
    download: false,
    print: false,
    search: false,
    jumpToPage: true,
    selectableRows: "none",
    rowsPerPage: rowsPerPage,
    rowsPerPageOptions: [5, 10, 20, 50],
    page: page,
    onTableChange: (action, tableState) => {
      switch (action) {
        case "changePage":
          setPage(tableState.page);
          break;
        case "changeRowsPerPage":
          setRowsPerPage(tableState.rowsPerPage);
          setPage(0);
          break;
        case "sort":
          // Guard against an empty/"none" sort (e.g. unsorting a column) -
          // the backend crashes on an empty sort property name, so fall
          // back to the default sort instead of sending a blank one.
          if (
            tableState.sortOrder?.name &&
            tableState.sortOrder?.direction &&
            tableState.sortOrder.direction !== "none"
          ) {
            setSort(tableState.sortOrder);
          } else {
            setSort({ name: "givenDate", direction: "DESC" });
          }
          break;
        default:
          break;
      }
    },
  };

  return (
    <>
      <div>
        <Toaster />
      </div>

      <div className="block-header block-header-padding">
        <div className="row clearfix">
          <div className="col-md-6 col-sm-12">
            <h1>All Feedbacks</h1>
          </div>
          <div className="col-md-6 col-sm-12 text-right">
            <button className="btn btn-default" onClick={() => navigate(-1)}>
              Back
            </button>
          </div>
        </div>
      </div>

      <div className="container-fluid mt-4">
        <div className="row clearfix">
          <div className="col-lg-12 col-md-12 col-sm-12 p-l-5 p-r-10">
            <div className="card">
              <div className="body">
                <div className="input-group mb-3">
                  <div className="input-group w-100 justify-content-end align-items-center">
                    <div className="select-branch-dropdown-wrapper mr-2">
                      <select
                        className="custom-select modal-select-field select-branch-dropdown"
                        id="inputGroupSelect01"
                        value={branchDropDValue}
                        onChange={(e) => {
                          const idx = e.target.value;
                          setBranchDropDValue(idx);
                          if (idx !== "" && allBranchesList[idx]) {
                            setSelectedBranchId(allBranchesList[idx].id);
                          } else {
                            setSelectedBranchId(
                              allBranchesList[0]?.id || userDetails?.bid || null
                            );
                          }
                          setPage(0);
                        }}
                      >
                        <option defaultValue hidden value="">
                          Select Branch...
                        </option>
                        {allBranchesList.map((item, index) => (
                          <option value={index} key={item.id}>
                            {item.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <label className="my-0 mr-2">From</label>
                    <DatePicker
                      className="input-sm form-control"
                      selected={startDate}
                      maxDate={endDate}
                      onChange={handleStartDateChange}
                    />
                    <label className="my-0 mx-3">To</label>
                    <DatePicker
                      className="input-sm form-control"
                      selected={endDate}
                      minDate={startDate}
                      onChange={handleEndDateChange}
                    />
                  </div>
                </div>

                <MUIDataTable
                  title={
                    isLoading ? (
                      <CircularProgress size={20} />
                    ) : (
                      <p className="my-0">{`${count || 0} records.`}</p>
                    )
                  }
                  data={list}
                  columns={columns}
                  options={options}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <FeedbackDetailsModal
        showFeedbackDetailsModal={showFeedbackDetailsModal}
        setShowFeedbackDetailsModal={setShowFeedbackDetailsModal}
        data={selectedFeedback}
        details={feedbackDetails}
        isDetailsLoading={isDetailsLoading}
      />
    </>
  );
};

export default AllFeedbacks;
